/** Préfixe des compteurs kills sauvages par route (`kills_g{gen}z{zone}`). */
export const ZONE_MASTERY_KILL_STAT_PREFIX = "kills_g";

/** Seuils de kills sauvages par zone (cumulés sur la route). */
export const ZONE_MASTERY_THRESHOLDS = [100, 500, 2000, 5000, 15000];

const ZONE_MASTERY_KILL_STAT_RE = /^kills_g(\d+)z(\d+)$/;

/**
 * Bonus actifs au palier atteint : chaque ligne inclut les effets des paliers précédents
 * (or remplacé au palier 5 par ×1,20, pas multiplié avec ×1,10).
 */
export const ZONE_MASTERY_BONUSES = [
  {
    level: 1,
    goldMult: 1.1,
    shinyMult: 1,
    dpsMult: 1,
    candyMult: 1,
    labelFr: "Palier 1 — ×1,10 or (compte, tout contenu)",
    labelEn: "Tier 1 — ×1.10 gold (account-wide, all content)",
  },
  {
    level: 2,
    goldMult: 1.1,
    shinyMult: 1.05,
    dpsMult: 1,
    candyMult: 1,
    labelFr: "Palier 2 — + ×1,05 shiny (conserve l’or)",
    labelEn: "Tier 2 — + ×1.05 shiny (keeps gold)",
  },
  {
    level: 3,
    goldMult: 1.1,
    shinyMult: 1.05,
    dpsMult: 1,
    candyMult: 1.15,
    labelFr: "Palier 3 — +15 % bonbons à l’achat (compte)",
    labelEn: "Tier 3 — +15% candies when buying (account-wide)",
  },
  {
    level: 4,
    goldMult: 1.1,
    shinyMult: 1.05,
    dpsMult: 1.1,
    candyMult: 1.15,
    labelFr: "Palier 4 — ×1,10 DPS (conserve or, shiny, bonbons)",
    labelEn: "Tier 4 — ×1.10 DPS (keeps gold, shiny, candies)",
  },
  {
    level: 5,
    goldMult: 1.2,
    shinyMult: 1.05,
    dpsMult: 1.1,
    candyMult: 1.15,
    labelFr: "Palier 5 — or ×1,20 (remplace ×1,10 ; autres bonus conservés)",
    labelEn: "Tier 5 — ×1.20 gold (replaces ×1.10; other bonuses kept)",
  },
];

export function getZoneMasteryLevel(kills) {
  let level = 0;
  for (const threshold of ZONE_MASTERY_THRESHOLDS) {
    if (kills >= threshold) level++;
    else break;
  }
  return level;
}

/** @param {Record<string, unknown> | null | undefined} stats */
export function listZoneMasteryRouteKills(stats) {
  const rows = [];
  if (!stats || typeof stats !== "object") return rows;
  for (const [key, raw] of Object.entries(stats)) {
    const match = ZONE_MASTERY_KILL_STAT_RE.exec(key);
    if (!match) continue;
    const gen = Math.floor(Number(match[1]));
    const zone = Math.floor(Number(match[2]));
    if (!Number.isFinite(gen) || !Number.isFinite(zone)) continue;
    if (gen < 1 || gen > 9 || zone < 1 || zone > 20) continue;
    const kills = Math.max(0, Math.floor(Number(raw) || 0));
    rows.push({ gen, zone, kills, level: getZoneMasteryLevel(kills) });
  }
  rows.sort((a, b) => a.gen - b.gen || a.zone - b.zone);
  return rows;
}

/** @param {Record<string, unknown> | null | undefined} stats */
export function sumZoneMasteryKillsFromStats(stats) {
  return listZoneMasteryRouteKills(stats).reduce((sum, row) => sum + row.kills, 0);
}

/** @param {Record<string, unknown> | null | undefined} stats */
export function getAccountZoneMasteryLevel(stats) {
  let maxLevel = 0;
  for (const row of listZoneMasteryRouteKills(stats)) {
    if (row.level > maxLevel) maxLevel = row.level;
  }
  return maxLevel;
}

/** @param {Record<string, unknown> | null | undefined} stats */
export function getAccountZoneMasteryProgress(stats) {
  const rows = listZoneMasteryRouteKills(stats);
  const level = getAccountZoneMasteryLevel(stats);
  if (rows.length === 0) {
    return {
      level: 0,
      bestRouteKills: 0,
      nextThreshold: ZONE_MASTERY_THRESHOLDS[0] ?? null,
      prevThreshold: 0,
    };
  }
  const bestRouteKills = Math.max(...rows.map((r) => r.kills));
  const nextThreshold =
    level >= ZONE_MASTERY_THRESHOLDS.length
      ? null
      : (ZONE_MASTERY_THRESHOLDS[level] ?? null);
  const prevThreshold =
    level > 0 ? (ZONE_MASTERY_THRESHOLDS[level - 1] ?? 0) : 0;
  return { level, bestRouteKills, nextThreshold, prevThreshold };
}

export function getZoneMasteryBonusesForLevel(level) {
  if (level <= 0) return null;
  return (
    ZONE_MASTERY_BONUSES[Math.min(level, ZONE_MASTERY_BONUSES.length) - 1] ??
    null
  );
}

/** Quantité de bonbons crédités pour un achat (bonus maîtrise palier 3+). */
export function getZoneMasteryCandyQtyPurchased(baseQty, masteryLevel) {
  const row = getZoneMasteryBonusesForLevel(masteryLevel);
  if (!row || row.candyMult <= 1) return baseQty;
  const safeQty = Math.max(0, Math.floor(baseQty));
  const bonus = safeQty * (row.candyMult - 1);
  return safeQty + Math.floor(bonus + 1e-9);
}

export function resolveZoneMasteryCombatContext(user) {
  const gen = Math.max(
    1,
    Math.min(
      9,
      Math.floor(Number(user.combatGeneration ?? user.currentGeneration ?? 1)),
    ),
  );
  const zone = Math.max(
    1,
    Math.min(20, Math.floor(Number(user.combatZone ?? user.currentZone ?? 1))),
  );
  const stats = user.stats ?? {};
  const kills = Math.max(
    0,
    Math.floor(Number(stats[`kills_g${gen}z${zone}`] ?? 0)),
  );
  return { gen, zone, kills, level: getZoneMasteryLevel(kills) };
}

/** Maîtrise bonus bonbons — palier compte (global). */
export function resolveZoneMasteryCandyPurchaseContext(user) {
  const stats = user.stats ?? {};
  const level = getAccountZoneMasteryLevel(stats);
  const progress = getAccountZoneMasteryProgress(stats);
  const rows = listZoneMasteryRouteKills(stats);
  const best =
    rows.length === 0
      ? null
      : rows.reduce((a, b) => (b.kills > a.kills ? b : a), rows[0]);
  return {
    gen: best?.gen ?? Math.max(1, Math.floor(Number(user.currentGeneration ?? 1))),
    zone: best?.zone ?? Math.max(1, Math.floor(Number(user.currentZone ?? 1))),
    kills: progress.bestRouteKills,
    level,
  };
}
